/**
 * SelfView - the local camera tile (top-right region). Mic and camera
 * controls live exclusively in the meeting toolbar (PR #89 removed the
 * duplicate self-view button row); this surface is just the tile.
 *
 * Hard contract: #local-video is a DIV that videochat.js drives
 * imperatively - Agora injects its own <video> child and videochat flips
 * `display` when the camera track attaches/detaches. React therefore
 * renders the div ONCE, declares NO children for it, and applies its styles
 * through a one-time ref callback - never a style prop, which would clobber
 * videochat's display writes on every re-render. The element is never
 * conditionally unmounted: role-gated visibility hides the whole container
 * via style (observers can't publish), and re-gates reactively on
 * sessionStore.userRole.
 */
import { useStore } from 'zustand';
import { can } from '../../config/roles.js';
import { sessionStore } from '../../state/sessionStore';

/** One-time styling for the Agora-owned tile; display stays videochat's. */
function styleLocalVideoTile(el: HTMLDivElement | null): void {
  if (!el || el.dataset.styled) return;
  el.dataset.styled = '1';
  Object.assign(el.style, {
    width: '200px',
    height: '150px',
    borderRadius: '8px',
    border: '2px solid #fff',
    overflow: 'hidden',
    background: '#000',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    display: 'none',
  });
}

export function SelfView() {
  const userRole = useStore(sessionStore, (s) => s.userRole);
  // Observers can't publish - hide (never unmount: #local-video must survive).
  const visible = can(userRole, 'publishAudio');

  return (
    <div
      id="self-view"
      style={{
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        pointerEvents: 'auto',
      }}
    >
      <div id="local-video" ref={styleLocalVideoTile} />
    </div>
  );
}
